import React from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { routes } from '@mobi/account/Areas/SignUp/Utils/routes'
import { WITHDRAW_ROOT_ROUTE } from '@mobi/account/Areas/Withdraw/Utils'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { routes as accountRoutes } from '@mobi/account/Utils'
import { font } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { Constants, FeatureFlags, hexColors } from '@mobi/settings'
import { getEventGroupsFromRootQuery } from '@mobi/sports-offering/helpers/queries'
import { bottomSafeAreaInset, isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { AppRoutes } from '@core/AppRoutes'
import { useAccountDetailsQuery } from '@core/Areas/Account/helpers/useGetAccountDetails'
import { trackSideMenuItemClick } from '@core/Areas/AppHeader/helpers/analytics'
import { openDeposit } from '@core/Areas/Deposit'
import { useNewSportsOfferingFeature } from '@core/Areas/KambiSports/Offering/hooks/useNewSportsOfferingFeature'
import { showLogin } from '@core/Areas/Login/helpers/showLogin'
import { useLoginState } from '@core/Areas/Login/hooks'
import { useIsVenueModeActive } from '@core/Areas/VenueMode/Hooks/useIsVenueModeActive'
import { VENUE_FEATURE_NAME } from '@core/Areas/VenueMode/constants'
import { PrimaryButtonStyled, SecondaryButtonStyled } from '@core/Components/Buttons/Buttons.styles'
import { PendingTicketsCountProvider } from '@core/Data/Account/PendingTicketsCount/PendingTicketsCountProvider'
import { keys } from '@core/Services/Analytics/keys'
import { trackEvent } from '@core/Services/Analytics/track'
import { state$ as navigationState$ } from '@core/State/Navigation/driver'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { store } from '@core/Store'
import { useFeature, useObservableImmutable } from '@core/Utils/hooks'
import { appHistory } from '@core/Utils/routing/history'
import { toggleIsDropDownOpen } from '../../Store'
import { Badge } from '../Common/Badge'
import { BalanceSection } from './Components/BalanceSection/BalanceSection'
import { Greeting } from './Components/Greeting/Greeting'
import { HamburgerMenuExternalLink, HamburgerMenuLink } from './Components/HamburgerMenuLink'
import {
  AccountDetailsSectionStyled,
  AccountNameLabelStyled,
  AccountNumberLabelStyled,
  CloseButtonStyled,
  DepositWithdrawSectionStyled,
  HamburgerMenuSectionHeaderStyled,
  JustifiedLinkText,
  LoginSignUpSectionStyled,
  SeparatorStyled,
} from './HamburgerMenu.styles'

const enum LocalConstants {
  LoadingText = 'Loading...',
  TABtouchExclusiveTermKey = 'tabtouch_exclusives__rwwa_',
}

export const HamburgerMenu: React.FC<{
  onClose: () => void
}> = ({ onClose }) => {
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { isLoggedIn, campaigns } = useObservableImmutable(userAccountState$, [
    'isLoggedIn',
    'campaigns',
  ])

  const isAmlContactDetailsEnabled = useFeature(FeatureFlags.AML_CONTACT_DETAILS.key)
  const isBetLoopFeatureActive = useFeature(FeatureFlags.BET_LOOP.key)
  const isVenueModeActive = useIsVenueModeActive()
  const areVerificationAccountsActive = useFeature(FeatureFlags.AML_SIGNUP_RISK_ASSESSMENT.key)

  const isKambiSportsOfferingEnabled = useNewSportsOfferingFeature()

  const { data: accountDetails } = useAccountDetailsQuery(true)

  const { data: sportsData } = useQuery(getEventGroupsFromRootQuery)

  const dataWithBetoffers = React.useMemo(
    () => (sportsData?.group?.groups ?? []).filter(sport => sport.boCount !== 0),
    [sportsData]
  )

  const tabtouchExclusive = dataWithBetoffers.find(
    group => group.termKey === LocalConstants.TABtouchExclusiveTermKey
  )

  const {
    accountName = LocalConstants.LoadingText,
    accountNumber = LocalConstants.LoadingText,
    blackBookAlertCount = 0,
  } = accountDetails || {}

  const campaignsCount = campaigns?.count() ?? 0

  const { handlers } = useLoginState()

  React.useEffect(() => {
    const subscription = navigationState$
      .map(navState => navState.toJS().currentUrl)
      .skip(1)
      .distinctUntilChanged()
      .subscribe(onClose)
    return () => subscription?.dispose()
  }, [onClose])

  React.useEffect(() => {
    store.dispatch(toggleIsDropDownOpen(false))
  }, [])

  return (
    <HamburgerMenuStyled
      ref={containerRef}
      id='hamburger-menu-container'
      className='hamburger-menu'
      data-tid-hamburger-menu=''
      role='dialog'
    >
      <CloseButtonStyled
        role='button'
        id='hamburger-close'
        onClick={() => {
          trackSideMenuItemClick('hamburger-close-button')
          onClose()
        }}
      >
        <Icon type='cross' color={hexColors.studio} size='1rem' title='Close menu' />
      </CloseButtonStyled>

      {isLoggedIn && (
        <AccountDetailsSectionStyled>
          <Greeting />
          <AccountNameLabelStyled>{accountName}</AccountNameLabelStyled>
          <AccountNumberLabelStyled>Account # {accountNumber}</AccountNumberLabelStyled>
        </AccountDetailsSectionStyled>
      )}

      {!isLoggedIn && (
        <LoginSignUpSectionStyled>
          <SecondaryButtonStyled
            id='hamburger-sign-up'
            onClick={() => {
              trackSideMenuItemClick('hamburger-sign-up')
              onClose()
              handlers.signUp()
              signupTracker({
                interaction_type: 'Sign Up Clicked',
                signUpFlow: 'Standard',
                location: 'Hamburger',
              })
            }}
          >
            Sign up
          </SecondaryButtonStyled>
          <PrimaryButtonStyled
            id='hamburger-login'
            onClick={() => {
              trackSideMenuItemClick('hamburger-login')
              onClose()
              showLogin()
            }}
          >
            Log in
          </PrimaryButtonStyled>
        </LoginSignUpSectionStyled>
      )}

      {isLoggedIn && (
        <>
          <SeparatorStyled />
          <BalanceSection />
        </>
      )}

      {isLoggedIn && (
        <>
          <SeparatorStyled />
          <DepositWithdrawSectionStyled>
            <SecondaryButtonStyled
              id='hamburger-deposit'
              onClick={() => {
                trackSideMenuItemClick('hamburger-deposit')
                onClose()
                openDeposit()
              }}
            >
              <Icon type='arrowdownwithtail' size='1.5rem' color={hexColors.studio} />
              Deposit
            </SecondaryButtonStyled>

            <SecondaryButtonStyled
              id='hamburger-withdraw'
              href=''
              onClick={e => {
                e.preventDefault()
                appHistory.push(WITHDRAW_ROOT_ROUTE)
                trackSideMenuItemClick('hamburger-withdraw')
                onClose()
              }}
            >
              <Icon type='arrowupwithtail' size='1.5rem' color={hexColors.studio} />
              Withdraw
            </SecondaryButtonStyled>
          </DepositWithdrawSectionStyled>
        </>
      )}

      {(isLoggedIn || isReactNativeApp) && (
        <>
          <SeparatorStyled />
          <HamburgerMenuSectionHeaderStyled>My Bets</HamburgerMenuSectionHeaderStyled>
        </>
      )}

      {isLoggedIn && (
        <>
          <HamburgerMenuLink to='/account/activity/pending' id='hamburger-pending-bets'>
            <JustifiedLinkText>
              Pending bets
              <PendingTicketsCountProvider
                shouldForceRefetchPendingCount
                render={({ pendingBetsCount, doesCountExceedMax }) =>
                  pendingBetsCount && pendingBetsCount > 0 ? (
                    <Badge
                      count={pendingBetsCount}
                      showPlusIcon={doesCountExceedMax}
                      data-testid='hamburger-menu-pending-bets-badge'
                    />
                  ) : null
                }
              />
            </JustifiedLinkText>
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/activity/results' id='hamburger-resulted-bets'>
            Resulted bets
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/blackbook' id='hamburger-blackbook'>
            <JustifiedLinkText>
              Blackbook
              {blackBookAlertCount > 0 && (
                <Badge count={blackBookAlertCount} data-testid='hamburger-blackbook-badge' />
              )}
            </JustifiedLinkText>
          </HamburgerMenuLink>
        </>
      )}

      {isLoggedIn && (
        <HamburgerMenuLink to='/account/activity/transactions' id='hamburger-debit-credit'>
          Debit/credit
        </HamburgerMenuLink>
      )}

      <SeparatorStyled />

      <HamburgerMenuSectionHeaderStyled>Racing</HamburgerMenuSectionHeaderStyled>

      <HamburgerMenuLink to='/next-events' id='hamburger-next-races'>
        Next races
      </HamburgerMenuLink>

      <HamburgerMenuLink to='/tote?code=races' id='hamburger-races'>
        Races
      </HamburgerMenuLink>

      <HamburgerMenuLink to='/tote?code=trots' id='hamburger-trots'>
        Trots
      </HamburgerMenuLink>

      <HamburgerMenuLink to='/tote?code=dogs' id='hamburger-dogs'>
        Dogs
      </HamburgerMenuLink>

      {isBetLoopFeatureActive && (
        <HamburgerMenuLink
          to={AppRoutes.BetLoop}
          data-testid='expert-hub-link'
          onClick={() => trackSideMenuItemClick('hamburger-follow-me')}
        >
          Bet Loop
        </HamburgerMenuLink>
      )}

      <HamburgerMenuLink to='/favourite-numbers' id='hamburger-favourite-numbers'>
        Favourite numbers
      </HamburgerMenuLink>

      <HamburgerMenuLink to='/tote/mystery' id='hamburger-mystery-bet'>
        Mystery bet
      </HamburgerMenuLink>

      <HamburgerMenuLink to='/jackpots' id='hamburger-jackpots'>
        Jackpots
      </HamburgerMenuLink>

      <HamburgerMenuLink to='/goodthings' id='hamburger-good-things'>
        Good things
      </HamburgerMenuLink>

      <SeparatorStyled />

      <HamburgerMenuSectionHeaderStyled>Sport</HamburgerMenuSectionHeaderStyled>
      {isKambiSportsOfferingEnabled ? (
        <>
          <HamburgerMenuLink to={AppRoutes.Sports} id='hamburger-all-sport'>
            All sports
          </HamburgerMenuLink>

          <HamburgerMenuLink to={AppRoutes.Sports + '?tab=upcoming'} id='hamburger-upcoming-sport'>
            Upcoming
          </HamburgerMenuLink>

          <HamburgerMenuLink to={AppRoutes.Sports + '?tab=live'} id='hamburger-live-sport'>
            Live
          </HamburgerMenuLink>

          <HamburgerMenuLink
            to='/tabtouch-sports/bethistory/active/rewards'
            id='hamburger-sport-specials'
          >
            Specials
          </HamburgerMenuLink>

          {tabtouchExclusive?.groups && tabtouchExclusive.groups.length > 0 && (
            <HamburgerMenuLink
              to='/tabtouch-sports/sports-hub/tabtouch_exclusives__rwwa_'
              id='hamburger-sport-tt-exclusive'
            >
              TABtouch exclusives
            </HamburgerMenuLink>
          )}

          <HamburgerMenuLink to='/totesports' id='hamburger-sport-tipping'>
            Tipping
          </HamburgerMenuLink>
        </>
      ) : (
        <>
          <HamburgerMenuLink to='/tabtouch-sports/starting-soon' id='hamburger-next-sport'>
            Next sport
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/tabtouch-sports/in-play' id='hamburger-live-sport'>
            Live sport
          </HamburgerMenuLink>

          <HamburgerMenuLink to={AppRoutes.SportsRoot} id='hamburger-all-sport'>
            All sport
          </HamburgerMenuLink>
        </>
      )}

      <SeparatorStyled />

      <HamburgerMenuSectionHeaderStyled>Venue</HamburgerMenuSectionHeaderStyled>

      {isVenueModeActive && (
        <HamburgerMenuLink to='/venue-mode' id='hamburger-venue-mode'>
          {VENUE_FEATURE_NAME} <em>New</em>
        </HamburgerMenuLink>
      )}

      {isReactNativeApp && (
        <HamburgerMenuLink
          to='/'
          id='hamburger-scan-my-bet'
          onClick={e => {
            trackSideMenuItemClick('hamburger-scan-my-bet')
            e.preventDefault()
            sendToNative('SCAN_RETAIL_TICKET')
            onClose()
          }}
        >
          Scan my bet
        </HamburgerMenuLink>
      )}

      <HamburgerMenuLink
        to='/tablocator'
        id='hamburger-find-a-tab'
        onClick={e => {
          trackSideMenuItemClick('hamburger-find-a-tab')
          if (isReactNativeApp) {
            e.preventDefault()
            sendToNative('REQUEST_NATIVE_NAVIGATION', { location: 'TAB Locator' })
            onClose()
          }
        }}
      >
        Find my TAB
      </HamburgerMenuLink>

      <SeparatorStyled />

      <HamburgerMenuSectionHeaderStyled>Insider Tips</HamburgerMenuSectionHeaderStyled>

      <HamburgerMenuExternalLink
        href='https://blog.tabtouch.com.au/'
        id='hamburger-blog'
        onClick={e => {
          trackSideMenuItemClick('hamburger-blog')
          if (!isReactNativeApp) {
            return
          }
          e.preventDefault()
          sendToNative('REQUEST_NATIVE_NAVIGATION', { location: 'Blog' })
        }}
      >
        Blog
      </HamburgerMenuExternalLink>

      <SeparatorStyled />

      {isLoggedIn && (
        <>
          <HamburgerMenuSectionHeaderStyled tabIndex={undefined}>
            Profile
          </HamburgerMenuSectionHeaderStyled>

          {campaignsCount > 0 && (
            <HamburgerMenuLink to='/account/offer' id='hamburger-bonus-offers'>
              Bonuses
            </HamburgerMenuLink>
          )}

          <HamburgerMenuLink to='/account/settings' id='hamburger-settings'>
            Settings
          </HamburgerMenuLink>

          <HamburgerMenuLink
            to={
              isAmlContactDetailsEnabled ? accountRoutes.personalDetails : '/account/contactdetails'
            }
            id='hamburger-personal-details'
          >
            Personal Details
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/marketingcommunications' id='hamburger-subscription'>
            Subscription
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/change-password' id='hamburger-change-password'>
            Change password
          </HamburgerMenuLink>
          <SeparatorStyled />
        </>
      )}

      <HamburgerMenuSectionHeaderStyled tabIndex={undefined}>
        Support
      </HamburgerMenuSectionHeaderStyled>

      <HamburgerMenuLink to='/contactus' id='hamburger-contact-us'>
        Contact us
      </HamburgerMenuLink>

      <HamburgerMenuExternalLink
        href='https://help.tabtouch.com.au'
        id='hamburger-help-and-support'
        onClick={() => {
          trackEvent(keys.setPageVisit, {
            customURL: 'https://help.tabtouch.com.au',
            pageTitle: 'HELP',
          })
        }}
      >
        Help & support
      </HamburgerMenuExternalLink>

      <HamburgerMenuExternalLink
        href='https://www.rwwa.com.au/privacy-policy/'
        id='hamburger-privacy-policy'
      >
        Privacy policy
      </HamburgerMenuExternalLink>

      {isReactNativeApp && (
        <HamburgerMenuLink
          to='/'
          id='hamburger-what-is-new'
          onClick={e => {
            trackSideMenuItemClick('hamburger-what-is-new')
            e.preventDefault()
            sendToNative('REQUEST_NATIVE_NAVIGATION', { location: "What's New" })
            onClose()
          }}
        >
          What's new
        </HamburgerMenuLink>
      )}

      <SeparatorStyled />

      <HamburgerMenuSectionHeaderStyled tabIndex={undefined}>
        Responsible wagering
      </HamburgerMenuSectionHeaderStyled>

      {isLoggedIn && (
        <>
          <HamburgerMenuLink to='/account/deposit-limits' id='hamburger-deposit-limits'>
            Deposit limit
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/precommitment' id='hamburger-bet-limits'>
            Bet limit
          </HamburgerMenuLink>

          <HamburgerMenuLink to={accountRoutes.blockDebitCard} id='hamburger-block-debit-card'>
            Block debit card
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/take-a-break' id='hamburger-take-a-break'>
            Take a break
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/close' id='hamburger-close-account'>
            Close account
          </HamburgerMenuLink>

          <HamburgerMenuLink to='/account/activity-statements' id='hamburger-activity-statement'>
            Statements
          </HamburgerMenuLink>
        </>
      )}

      <HamburgerMenuExternalLink
        href={Constants.ResponsibleWageringURL}
        id='hamburger-responsible-wagering'
      >
        Responsible wagering
      </HamburgerMenuExternalLink>

      {areVerificationAccountsActive && !isLoggedIn && (
        <>
          <SeparatorStyled />
          <HamburgerMenuSectionHeaderStyled tabIndex={undefined}>
            Identity Verification
          </HamburgerMenuSectionHeaderStyled>

          <HamburgerMenuLink
            to={routes.verificationAccountInformation}
            id='hamburger-verification-account'
            onClick={() => {
              signupTracker({
                interaction_type: 'Sign Up Clicked',
                signUpFlow: 'ID Verification',
                location: 'Hamburger',
              })
              onClose()
            }}
          >
            Verify my ID
          </HamburgerMenuLink>
        </>
      )}
    </HamburgerMenuStyled>
  )
}

// ======
// Styles
// ======

const HamburgerMenuStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: '1rem',
  backgroundColor: hexColors.white,
  fontFamily: font.family.secondary,
  paddingBottom: bottomSafeAreaInset,
})
